/**
 * Investor-Portal — Aufwendungen-Page (#1146 v2.499.0).
 * Plan-vs-IST-Linien-Charts in 3 Granularitäten:
 *   - Kategorie (4 Charts: Material/Personal/Sonstige/Abschreibungen)
 *   - Sub-Aggregat (9 Charts: 5a/5b/5c/5d, 6a/6b/6c, 7, 8)
 *   - Top-10 Konten (Aufwand-Konten 7xxx via Odoo RPC)
 * Drill-Down von GuV via ?focus=EBIT5|6|7|8 → setzt Granularität + filtert Charts.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import PeriodToggle, { buildPeriodQuery } from './components/PeriodToggle';

const API = import.meta.env.VITE_API_URL || '';
const MONTH_LABELS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function fmtEur(v) {
  if (v === null || v === undefined) return '–';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}
function fmtPct(v) {
  if (v === null || v === undefined) return '–';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)} %`;
}
function deltaColor(trend) {
  if (trend === 'up_good') return 'text-emerald-600';
  if (trend === 'down_bad') return 'text-red-600';
  return 'text-[#6B7280]';
}
function trendIcon(trend) {
  if (trend === 'up_good') return { glyph: '↗', color: '#10B981' };
  if (trend === 'down_bad') return { glyph: '↘', color: '#EF4444' };
  return { glyph: '→', color: '#9CA3AF' };
}

const FOCUS_GRANULARITY = {
  EBIT5: 'aggregate',
  EBIT6: 'aggregate',
  EBIT7: 'aggregate',
  EBIT8: 'top10',
};

const FOCUS_LABEL = {
  EBIT5: '5. Material & bezogene Leistungen',
  EBIT6: '6. Personalaufwand',
  EBIT7: '7. Abschreibungen',
  EBIT8: '8. Sonstige betriebliche Aufwendungen',
};

export default function InvestorAufwendungen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();

  const initialFocus = searchParams.get('focus') || null;
  const initialGranularity = initialFocus && FOCUS_GRANULARITY[initialFocus]
    ? FOCUS_GRANULARITY[initialFocus]
    : 'kategorie';

  const [periodState, setPeriodState] = useState(() => {
    const p = searchParams.get('period') || 'ytd';
    const y = parseInt(searchParams.get('year') || currentYear, 10);
    const m = parseInt(searchParams.get('month') || new Date().getMonth() + 1, 10);
    const q = parseInt(searchParams.get('quarter') || Math.ceil((new Date().getMonth() + 1) / 3), 10);
    return {
      period: p,
      year: y,
      month: m,
      quarter: q,
      fromDate: searchParams.get('from_date') || null,
      toDate: searchParams.get('to_date') || null,
    };
  });
  const [granularity, setGranularity] = useState(initialGranularity);
  const [focus, setFocus] = useState(initialFocus);
  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const periodQuery = useMemo(() => buildPeriodQuery(periodState), [periodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const q = new URLSearchParams(periodQuery);
        q.set('granularity', granularity);
        if (focus) q.set('focus', focus);
        const res = await fetch(`${API}/api/investor/aufwendungen?${q.toString()}`, { credentials: 'include' });
        const data = await res.json();
        if (abort) return;
        if (!res.ok) {
          setError(data.error || `HTTP ${res.status}`);
          setCharts([]);
        } else {
          setCharts(data.charts || []);
        }
      } catch (e) {
        if (!abort) setError(e.message);
      } finally {
        if (!abort) setLoading(false);
      }
    }
    load();
    return () => { abort = true; };
  }, [periodQuery, granularity, focus]);

  function changeGranularity(g) {
    setGranularity(g);
    setFocus(null);
    const next = new URLSearchParams(periodQuery);
    next.delete('focus');
    setSearchParams(next, { replace: true });
  }

  function clearFocus() {
    setFocus(null);
    setGranularity('kategorie');
    const next = new URLSearchParams(periodQuery);
    next.delete('focus');
    setSearchParams(next, { replace: true });
  }

  const totals = useMemo(() => {
    const planSum = charts.reduce((s, c) => s + (c.plan_ytd || 0), 0);
    const istSum = charts.reduce((s, c) => s + (c.ist_ytd || 0), 0);
    const deltaAbs = istSum - planSum;
    const deltaPct = planSum !== 0 ? (deltaAbs / Math.abs(planSum)) * 100 : null;
    return { planSum, istSum, deltaAbs, deltaPct };
  }, [charts]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A2E]">Aufwendungen</h1>
          <div className="text-sm text-[#6B7280] mt-1">
            Plan-vs-IST-Monatslinien für Aufwand-Positionen
            {focus && FOCUS_LABEL[focus] && (
              <> · <span className="text-[#338edc] font-medium">Fokus: {FOCUS_LABEL[focus]}</span></>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodToggle
            period={periodState.period}
            year={periodState.year}
            month={periodState.month}
            quarter={periodState.quarter}
            fromDate={periodState.fromDate}
            toDate={periodState.toDate}
            onChange={setPeriodState}
          />
          <div className="bg-[#F8F9FC] rounded-[10px] p-[3px] border border-[#E5E7EB] flex items-center gap-0.5">
            {[
              { id: 'kategorie', label: 'Kategorie' },
              { id: 'aggregate', label: 'Sub-Aggregat' },
              { id: 'top10', label: 'Top-10 Konten' },
            ].map(g => (
              <button
                key={g.id}
                onClick={() => changeGranularity(g.id)}
                className={`px-3 py-1 text-xs font-medium rounded-md ${
                  granularity === g.id ? 'bg-white text-[#1A1A2E] shadow-sm' : 'text-[#6B7280] hover:text-[#1A1A2E]'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {focus && (
        <div className="flex items-center justify-between bg-[#338edc]/5 border border-[#338edc]/30 text-[#1A1A2E] text-xs px-3 py-2 rounded-lg">
          <span>
            Drill-Down aus GuV — gezeigt werden nur Charts zu <strong>{FOCUS_LABEL[focus] || focus}</strong>.
          </span>
          <div className="flex items-center gap-3">
            <button onClick={clearFocus} className="text-[#338edc] hover:underline font-medium">
              Fokus entfernen
            </button>
            <button
              onClick={() => navigate(`/investor/guv?${periodQuery}`)}
              className="text-[#338edc] hover:underline font-medium"
            >
              ← Zurück zur GuV
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">
          Fehler: {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Plan YTD" value={fmtEur(totals.planSum)} sub={`${charts.length} Positionen`} />
        <KpiTile label="IST YTD" value={fmtEur(totals.istSum)} />
        <KpiTile
          label="Δ absolut"
          value={fmtEur(totals.deltaAbs)}
          valueClass={totals.deltaAbs > 0 ? 'text-red-600' : totals.deltaAbs < 0 ? 'text-emerald-600' : 'text-[#1A1A2E]'}
        />
        <KpiTile
          label="Δ relativ"
          value={fmtPct(totals.deltaPct)}
          valueClass={totals.deltaPct > 0 ? 'text-red-600' : totals.deltaPct < 0 ? 'text-emerald-600' : 'text-[#1A1A2E]'}
        />
      </div>

      {loading && charts.length === 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="bg-white border border-[#E5E7EB] rounded-xl p-4 h-[300px] animate-pulse" />
          ))}
        </div>
      )}

      {!loading && charts.length === 0 && !error && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center text-sm text-[#6B7280]">
          Keine Daten für diese Periode/Granularität.
        </div>
      )}

      {charts.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {charts.map(c => (
            <ChartCard key={c.chart_id} chart={c} />
          ))}
        </div>
      )}

      {charts.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Detail-Tabelle</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Position</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-32">Plan YTD</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-32">IST YTD</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-28">Δ abs</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-20">Δ %</th>
                  <th className="text-center px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-32">Verlauf</th>
                  <th className="text-center px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-14">Trend</th>
                </tr>
              </thead>
              <tbody>
                {charts.map(c => {
                  const t = trendIcon(c.trend);
                  return (
                    <tr key={c.chart_id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                      <td className="px-3 py-2 text-[#1A1A2E]">
                        {c.position_name}
                        {c.plan_is_estimated && (
                          <span className="ml-1.5 text-[10px] text-[#9CA3AF]" title="Plan anteilig vom EBIT8-Plan basierend auf IST-Anteil">
                            (Plan geschätzt)
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEur(c.plan_ytd)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E] font-medium">{fmtEur(c.ist_ytd)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${deltaColor(c.trend)}`}>{fmtEur(c.delta_abs)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${deltaColor(c.trend)}`}>{fmtPct(c.delta_pct)}</td>
                      <td className="px-3 py-2">
                        <Sparkline values={c.ist_monthly} />
                      </td>
                      <td className="px-3 py-2 text-center text-base" style={{ color: t.color }}>{t.glyph}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-xs text-[#9CA3AF]">
        Quelle: Odoo UGB-Bericht § 231 (AT) · Top-10-Konten via Odoo XML-RPC (account.code = 7%).
        Plan-Werte aus Investor-Excel. Bei Top-10-Konten ist Plan anteilig vom EBIT8-Plan hochgerechnet.
      </div>
    </div>
  );
}

function KpiTile({ label, value, sub, valueClass }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="text-[11px] text-[#6B7280] font-medium uppercase tracking-wide">{label}</div>
      <div className={`mt-1 text-xl font-semibold ${valueClass || 'text-[#1A1A2E]'}`}>{value}</div>
      {sub && <div className="mt-1 text-[11px] text-[#9CA3AF]">{sub}</div>}
    </div>
  );
}

function ChartCard({ chart }) {
  const data = MONTH_LABELS.map((label, i) => ({
    monat: label,
    Plan: chart.plan_monthly[i] ?? null,
    IST: chart.ist_monthly[i],
  }));
  const t = trendIcon(chart.trend);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#1A1A2E] truncate" title={chart.position_name}>
            {chart.position_name}
          </div>
          <div className="text-[11px] text-[#9CA3AF] mt-0.5">
            Plan {fmtEur(chart.plan_ytd)} · IST {fmtEur(chart.ist_ytd)}
            {chart.plan_is_estimated && <span className="ml-1.5">(Plan geschätzt)</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`text-xs font-medium tabular-nums ${deltaColor(chart.trend)}`}>{fmtPct(chart.delta_pct)}</span>
          <span className="text-base" style={{ color: t.color }}>{t.glyph}</span>
        </div>
      </div>
      <div style={{ width: '100%', height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="monat" stroke="#6B7280" fontSize={10} />
            <YAxis stroke="#6B7280" fontSize={10} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              formatter={(v) => fmtEur(v)}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="Plan" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="4 4" dot={false} connectNulls />
            <Line type="monotone" dataKey="IST" stroke="#338edc" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Sparkline({ values }) {
  const data = (values || []).map((v, i) => ({ i, v: v ?? null }));
  return (
    <div style={{ width: 110, height: 28 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <Line type="monotone" dataKey="v" stroke="#338edc" strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
